'use client';

/**
 * Per-line-item checkout notes.
 *
 * Notes are an order-time concern, not a durable cart attribute, so they live
 * in sessionStorage (keyed by `cartItemKey`) rather than the cart_items table
 * or the guest localStorage cart. sessionStorage keeps them across the
 * multi-step checkout flow (contact → shipping → review) and lets the cart
 * drawer and the order summary stay in sync through this shared provider,
 * while a fresh visit / cleared cart starts clean.
 *
 * Saved to the order at place-order time (order_items.notes).
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

const STORAGE_KEY = 'uqhoan_item_notes';

type ItemNotesValue = {
  /** Map of cartItemKey → note text. */
  notes: Record<string, string>;
  getNote: (key: string) => string;
  setNote: (key: string, value: string) => void;
  clearNotes: () => void;
};

const ItemNotesContext = createContext<ItemNotesValue | null>(null);

function readStored(): Record<string, string> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Record<string, string>) : {};
  } catch {
    return {};
  }
}

export function ItemNotesProvider({ children }: { children: React.ReactNode }) {
  const [notes, setNotes] = useState<Record<string, string>>({});

  // Hydrate from sessionStorage on mount (client only).
  useEffect(() => {
    setNotes(readStored());
  }, []);

  const persist = useCallback((next: Record<string, string>) => {
    if (typeof window !== 'undefined') {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    }
  }, []);

  const getNote = useCallback((key: string) => notes[key] ?? '', [notes]);

  const setNote = useCallback(
    (key: string, value: string) => {
      setNotes((prev) => {
        const next = { ...prev };
        if (value.trim() === '') {
          delete next[key];
        } else {
          next[key] = value;
        }
        persist(next);
        return next;
      });
    },
    [persist],
  );

  const clearNotes = useCallback(() => {
    setNotes({});
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  return (
    <ItemNotesContext.Provider value={{ notes, getNote, setNote, clearNotes }}>
      {children}
    </ItemNotesContext.Provider>
  );
}

export function useItemNotes() {
  const ctx = useContext(ItemNotesContext);
  if (!ctx) throw new Error('useItemNotes must be used inside ItemNotesProvider');
  return ctx;
}
